import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Icon } from '../components/Icon';
import { ProjectService } from '../services/projectService';
import { Project } from '../types';
import '../styles/QuotesPage.css';

export const QuotesPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'draft' | 'active' | 'archived'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'client' | 'created' | 'updated' | 'expires'>('updated');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    loadProjects();
  }, [user?.uid]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all projects (visible to all users)
      const data = await ProjectService.getAllProjects();
      setProjects(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects
    .filter((project) => {
      const matchesFilter = filter === 'all' || project.status === filter;
      const matchesSearch =
        project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.clientName.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesFilter && matchesSearch;
    })
    .sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'client':
          comparison = a.clientName.localeCompare(b.clientName);
          break;
        case 'created':
          comparison = a.createdAt.getTime() - b.createdAt.getTime();
          break;
        case 'updated':
          comparison = a.updatedAt.getTime() - b.updatedAt.getTime();
          break;
        case 'expires':
          const aExpires = a.expirationDate?.getTime() || 0;
          const bExpires = b.expirationDate?.getTime() || 0;
          comparison = aExpires - bExpires;
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

  const handleCreateQuote = () => {
    navigate('/quotes/new');
  };

  const handleEditQuote = (projectId: string) => {
    navigate(`/quotes/${projectId}/edit`);
  };

  const handleViewQuote = (projectId: string) => {
    navigate(`/quotes/${projectId}`);
  };

  const handleDeleteQuote = async (projectId: string) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;

    try {
      await ProjectService.deleteProject(projectId);
      setProjects(projects.filter((p) => p.id !== projectId));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getStatusColor = (status: string, isExpired?: boolean) => {
    if (isExpired) return '#dc2626'; // Bright red for expired
    switch (status) {
      case 'draft':
        return '#64748b'; // Slate gray
      case 'active':
        return '#059669'; // Emerald green
      case 'archived':
        return '#7c3aed'; // Purple
      default:
        return '#3b82f6'; // Blue
    }
  };

  const getStatusIcon = (status: string, isExpired?: boolean) => {
    if (isExpired) return 'alert';
    switch (status) {
      case 'draft':
        return 'edit';
      case 'active':
        return 'document';
      case 'archived':
        return 'package';
      default:
        return 'document';
    }
  };

  const getDaysUntilExpiration = (expirationDate?: Date) => {
    if (!expirationDate) return null;
    const now = new Date();
    const diffTime = expirationDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (date: Date) => {
    // Format date without timezone conversion
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC', // Force UTC to prevent timezone shifts
    });
  };

  if (loading) {
    return (
      <div className="quotes-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading quotes...</p>
        </div>
      </div>
    );
  }

  return (
    <AppLayout>
      <div className="quotes-page">
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
          <div className="header-content">
            <div>
              <h1>Quotes</h1>
              <p>Manage all your project quotes</p>
            </div>
            <button onClick={handleCreateQuote} className="create-button">
              + New Quote
            </button>
          </div>
        </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <div className="quotes-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name or client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="sort-controls">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="sort-select"
          >
            <option value="updated">Updated Date</option>
            <option value="created">Created Date</option>
            <option value="expires">Expiration Date</option>
            <option value="name">Name</option>
            <option value="client">Client</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="sort-order-button"
            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        <div className="filter-buttons">
          {['all', 'draft', 'active', 'archived'].map((status) => (
            <button
              key={status}
              className={`filter-button ${filter === status ? 'active' : ''}`}
              onClick={() => setFilter(status as any)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
              <span className="count">
                {status === 'all'
                  ? projects.length
                  : projects.filter((p) => p.status === status).length}
              </span>
            </button>
          ))}
        </div>
      </div>

      {filteredProjects.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <Icon name="document" size={64} />
          </div>
          <h2>No quotes yet</h2>
          <p>
            {searchTerm || filter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Create your first quote to get started'}
          </p>
          {!searchTerm && filter === 'all' && (
            <button onClick={handleCreateQuote} className="empty-cta">
              Create New Quote
            </button>
          )}
        </div>
      ) : (
        <div className="quotes-grid">
          {filteredProjects.map((project) => {
            const daysUntilExpiration = getDaysUntilExpiration(project.expirationDate);
            const isExpiringSoon = daysUntilExpiration !== null && daysUntilExpiration > 0 && daysUntilExpiration <= 7;

            return (
            <div key={project.id} className={`quote-card ${project.isExpired ? 'expired' : ''}`}>
              <div className="quote-card-header">
                <div className="quote-card-title">
                  <h3>{project.name}</h3>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span className="status-badge" style={{ backgroundColor: getStatusColor(project.status, project.isExpired), display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon name={getStatusIcon(project.status, project.isExpired)} size={14} color="#ffffff" />
                      {project.isExpired ? 'Expired' : project.status}
                    </span>
                    {isExpiringSoon && !project.isExpired && (
                      <span className="status-badge warning" style={{ backgroundColor: '#f59e0b', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Icon name="alert" size={12} color="#ffffff" /> {daysUntilExpiration}d left
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="quote-card-body">
                <div className="quote-info-row">
                  <span className="label">Client:</span>
                  <span className="value">{project.clientName}</span>
                </div>

                <div className="quote-info-row">
                  <span className="label">Items:</span>
                  <span className="value">
                    {(project.moments || []).reduce((total, moment) => {
                      const assetCount = (moment.assets || []).length;
                      return total + assetCount;
                    }, 0)} assets
                  </span>
                </div>

                <div className="quote-info-row">
                  <span className="label">Created:</span>
                  <span className="value">{formatDate(project.createdAt)}</span>
                </div>

                {project.expirationDate && (
                  <div className="quote-info-row">
                    <span className="label">Expires:</span>
                    <span className="value" style={{ color: project.isExpired ? '#ef4444' : isExpiringSoon ? '#f59e0b' : 'inherit' }}>
                      {formatDate(project.expirationDate)}
                      {project.isExpired && ' (Expired)'}
                    </span>
                  </div>
                )}

                {project.description && (
                  <div className="quote-description">{project.description}</div>
                )}
              </div>

              <div className="quote-card-actions">
                <button onClick={() => handleViewQuote(project.id)} className="action-button view">
                  View
                </button>
                <button onClick={() => handleEditQuote(project.id)} className="action-button edit">
                  Edit
                </button>
                <button onClick={() => handleDeleteQuote(project.id)} className="action-button delete">
                  Delete
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}
      </div>
    </AppLayout>
  );
};
